import type * as z from "@/lib/zod";
import { leagueLabel } from "@/components/odds-screen/live/view-model";
import { boardMode, resultRows, searchRanker, type SearchRanker } from "./derive";
import type { oddsScreenSearchSchema } from "./protocol";
import type { BoardView, Catalog, Entity, EventHeader, EventId, FamilyId, FamilySummary, LeagueId, LeagueSummary, RowId } from "./types";

export type SearchHit =
  | { readonly kind: "game"; readonly league: LeagueId; readonly event: EventHeader; readonly compact?: true }
  | {
      readonly kind: "player";
      readonly league: LeagueId;
      readonly rowId: RowId;
      readonly eventId: EventId;
      readonly event: EventHeader | undefined;
      readonly entity: Entity;
      readonly family: FamilyId;
      readonly familyLabel: string;
      readonly compact?: true;
    }
  | { readonly kind: "market"; readonly league: LeagueId; readonly family: FamilySummary }
  | { readonly kind: "league"; readonly league: LeagueSummary };

export type HeldBoard = { readonly league: LeagueId; readonly family: FamilySummary | undefined; readonly view: BoardView };

export type SearchIndex = {
  readonly league: LeagueId;
  readonly catalog: Catalog | null;
  readonly board: BoardView | null;
  readonly boardFamily: FamilySummary | undefined;
  readonly boards: readonly HeldBoard[];
};

type GameHit = Extract<SearchHit, { kind: "game" }>;
type PlayerHit = Extract<SearchHit, { kind: "player" }>;
type MarketHit = Extract<SearchHit, { kind: "market" }>;
type LeagueHit = Extract<SearchHit, { kind: "league" }>;

export type SearchResults = {
  readonly games: readonly GameHit[];
  readonly players: readonly PlayerHit[];
  readonly markets: readonly MarketHit[];
  readonly leagues: readonly LeagueHit[];
};

export type RemoteSearch = z.infer<typeof oddsScreenSearchSchema>;
type RemoteEvent = RemoteSearch["games"][number];

const DEFAULT_GAMES = 4;
const MAX_GAMES = 8;
const MAX_PLAYERS = 8;
const MAX_MARKETS = 6;
const MAX_LEAGUES = 6;

type Ranked<T> = { readonly hit: T; readonly rank: number };

function asTypedFirst<T>(hits: Ranked<T>[], max: number): T[] {
  return hits.sort((a, b) => b.rank - a.rank).slice(0, max).map(({ hit }) => hit);
}

function bestRank(rank: SearchRanker, texts: readonly (string | undefined)[]): number {
  return Math.max(...texts.map((text) => (text === undefined ? 0 : rank(text))));
}

function leagueBoards(index: SearchIndex): readonly HeldBoard[] {
  const primary: HeldBoard[] = index.board === null ? [] : [{ league: index.league, family: index.boardFamily, view: index.board }];
  const others = index.boards.filter((held) => held.league === index.league && held.view !== index.board);
  return [...primary, ...others];
}

function games(rank: SearchRanker | null, index: SearchIndex): readonly GameHit[] {
  const seen = new Set<string>();
  const hits: Ranked<GameHit>[] = [];
  for (const held of leagueBoards(index)) {
    for (const event of held.view.events.values()) {
      if (seen.has(event.id)) continue;
      seen.add(event.id);
      const found = rank === null ? 2 : bestRank(rank, [event.name, event.home, event.away]);
      if (found > 0) hits.push({ hit: { kind: "game", league: index.league, event, ...(found === 1 ? { compact: true } : {}) }, rank: found });
    }
  }
  hits.sort((a, b) => a.hit.event.startMs - b.hit.event.startMs);
  return asTypedFirst(hits, rank === null ? DEFAULT_GAMES : MAX_GAMES);
}

function listsPlayers(family: FamilySummary): boolean {
  const mode = boardMode(family);
  return family.kind === "props" || mode === "lines" || (mode === "results" && resultRows(family) === "entity");
}

function players(rank: SearchRanker | null, index: SearchIndex): readonly PlayerHit[] {
  if (rank === null) return [];
  const seen = new Set<string>();
  const hits: Ranked<PlayerHit>[] = [];
  let asTyped = 0;
  for (const { family, view } of leagueBoards(index)) {
    if (family === undefined || !listsPlayers(family)) continue;
    for (const id of view.rowIds) {
      const ref = view.rows.get(id);
      if (ref === undefined || ref.entity === null) continue;
      const found = rank(ref.entity.name);
      if (found === 0) continue;
      const key = `${family.id}|${ref.eventId}|${ref.entity.key}`;
      if (seen.has(key)) continue;
      seen.add(key);
      hits.push({
        hit: {
          kind: "player",
          league: index.league,
          rowId: id,
          eventId: ref.eventId,
          event: view.events.get(ref.eventId),
          entity: ref.entity,
          family: family.id,
          familyLabel: family.label,
          ...(found === 1 ? { compact: true } : {}),
        },
        rank: found,
      });
      if (found === 2 && ++asTyped === MAX_PLAYERS) return asTypedFirst(hits, MAX_PLAYERS);
    }
  }
  return asTypedFirst(hits, MAX_PLAYERS);
}

function markets(rank: SearchRanker | null, index: SearchIndex): readonly MarketHit[] {
  const { catalog } = index;
  if (rank === null || catalog === null) return [];
  const hits: Ranked<MarketHit>[] = [];
  const visit = (id: LeagueId) => {
    for (const family of catalog.leagues.get(id)?.families ?? []) {
      if (family.rows === 0 && family.events === 0) continue;
      const found = bestRank(rank, [family.label, family.group]);
      if (found > 0) hits.push({ hit: { kind: "market", league: id, family }, rank: found });
    }
  };
  visit(index.league);
  for (const id of catalog.leagueOrder) {
    if (id !== index.league) visit(id);
  }
  return asTypedFirst(hits, MAX_MARKETS);
}

function leagues(rank: SearchRanker | null, index: SearchIndex): readonly LeagueHit[] {
  if (index.catalog === null) return [];
  const hits: Ranked<LeagueHit>[] = [];
  for (const id of index.catalog.leagueOrder) {
    const league = index.catalog.leagues.get(id);
    if (league === undefined || league.events === 0) continue;
    const found = rank === null ? 2 : bestRank(rank, [leagueLabel(id), id]);
    if (found > 0) hits.push({ hit: { kind: "league", league }, rank: found });
  }
  return asTypedFirst(hits, MAX_LEAGUES);
}

export function searchOdds(query: string, index: SearchIndex): SearchResults {
  const rank = searchRanker(query);
  return {
    games: games(rank, index),
    players: players(rank, index),
    markets: markets(rank, index),
    leagues: leagues(rank, index),
  };
}

function remoteHeader(event: RemoteEvent): EventHeader {
  return { id: event.id, name: event.name, startMs: event.start, home: event.home, away: event.away, homeShort: null, awayShort: null, homeLogoUrl: null, awayLogoUrl: null, props: event.props };
}

export function mergeSearch(
  local: SearchResults,
  remote: RemoteSearch | null,
  ctx: { readonly league: LeagueId; readonly family: FamilyId | null; readonly catalog: Catalog | null },
): SearchResults {
  if (remote === null) return local;
  const remoteGames = remote.games.map((event): GameHit => ({ kind: "game", league: event.league, event: remoteHeader(event) }));
  const remotePlayers = remote.players.flatMap((player): PlayerHit[] => {
    const league = ctx.catalog?.leagues.get(player.league);
    const catalogFamilies = league?.families ?? [];
    const skipped = league?.skippedFamilies ?? [];
    const families = player.families.filter((id) => {
      if (skipped.some((entry) => entry.id === id)) return false;
      const summary = catalogFamilies.find((entry) => entry.id === id);
      return summary === undefined || listsPlayers(summary);
    });
    const [first] = families;
    if (first === undefined) return [];
    const family = ctx.family !== null && families.includes(ctx.family) ? ctx.family : first;
    const summary = catalogFamilies.find((entry) => entry.id === family);
    return [{
      kind: "player", league: player.league, rowId: `${player.event.id}|${player.key}`, eventId: player.event.id,
      event: remoteHeader(player.event), entity: { key: player.key, name: player.name }, family, familyLabel: summary?.label ?? family,
    }];
  });
  return {
    games: withRemote(local.games, remoteGames, (hit) => hit.event.id, MAX_GAMES),
    players: withRemote(local.players, remotePlayers, (hit) => `${hit.league}|${hit.eventId}|${hit.entity.key}`, MAX_PLAYERS),
    markets: local.markets,
    leagues: local.leagues,
  };
}

function withRemote<T extends { readonly compact?: true }>(local: readonly T[], remote: readonly T[], key: (hit: T) => string, max: number): T[] {
  const seen = new Set(local.map(key));
  const fresh: T[] = [];
  for (const hit of remote) {
    if (seen.has(key(hit))) continue;
    seen.add(key(hit));
    fresh.push(hit);
  }
  return [...local.filter((hit) => hit.compact !== true), ...fresh, ...local.filter((hit) => hit.compact === true)].slice(0, max);
}
